import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Player as RemotionPlayer, PlayerRef } from '@remotion/player';
import CaptionPresetPreviewComposition from './CaptionPresetPreviewComposition';
import { getPresetDisplayName } from '@/App/remotion/libs/caption/caption-presets/display-names';
import { CaptionPresetName } from '@/App/remotion/libs/caption/caption-presets/types';
import { classnames } from '@/libs/utils';

interface CaptionPresetPreviewCardProps {
  presetName: CaptionPresetName;
  isSelected: boolean;
  onClick: () => void;
}

const CAPTION_PREVIEW_CONTENT_DURATION_SECONDS = 5.5;
const FPS = 24;
const DURATION_IN_FRAMES = Math.ceil(CAPTION_PREVIEW_CONTENT_DURATION_SECONDS * FPS); // 132 frames

const presetSeekMap: Record<CaptionPresetName, number> = {
  beast: 25,
  snazzy: 20,
  sleek: 20,
  bouncy: 5,
  simpleBold: 40,
  sassy: 40,
  prof: 61,
  neo: 5,
  funky: 61,
  neon: 49,
  corporate: 25,
  marker: 40,
  romantic: 40,
  silicon: 20,
  sign: 5,
  custom: 40
};

function CaptionPresetPreviewCard({ presetName, isSelected, onClick }: CaptionPresetPreviewCardProps) {
  const playerRef = useRef<PlayerRef>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.seekTo(presetSeekMap[presetName] || presetSeekMap.custom);
    }
  }, [presetName]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (playerRef.current) {
      playerRef.current.seekTo(0);
      playerRef.current.play();
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (playerRef.current) {
      playerRef.current.pause();
      playerRef.current.seekTo(presetSeekMap[presetName] || presetSeekMap.custom);
    }
  }, [presetName]);

  const displayName = useMemo(() => getPresetDisplayName(presetName), [presetName]);

  return (
    <div
      className={classnames(
        'relative min-h-[119px] cursor-pointer overflow-hidden rounded-2xl border-2',
        isSelected ? 'border-slate-900' : 'border-slate-200'
      )}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <RemotionPlayer
        ref={playerRef}
        component={CaptionPresetPreviewComposition}
        inputProps={{ presetName, isHovered }}
        durationInFrames={DURATION_IN_FRAMES}
        compositionWidth={1920}
        compositionHeight={678}
        fps={FPS}
        loop={true}
        controls={false}
        clickToPlay={false}
        spaceKeyToPlayOrPause={false}
        style={{ width: '100%' }}
      />

      <div className="absolute left-2 top-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-2xs transition-colors duration-200">
        {displayName}
      </div>
    </div>
  );
}

export default memo(CaptionPresetPreviewCard);
